/**
 * @file `BudgetMeter` — the free-tier daily VM-time pill in the app-bar
 *       right cluster (design §7; #941), a live countdown since #1315.
 * @module @labcraft/lab/ui/components/budget-meter
 *
 * An honest, always-visible meter — "an honest meter the learner can
 * watch", not an invisible cap that only bites at the wall. Reads
 * {@link useBudget}; renders nothing while loading, on error, or for a
 * paid learner (the meter "simply disappears from the chrome").
 *
 * The countdown ticks in real time **only while the chapter's VM is
 * actively `running`** (the sole state that spends budget) — surfaced to
 * the chrome via {@link useReaderChrome}'s `vmRunning`. It extrapolates
 * from a server `usedMsToday` baseline, accruing local running time. The
 * baseline holds the **monotonic-downward** invariant: {@link useBudget}'s
 * 30s reconcile re-baselines only when the server reports MORE used than
 * local accrual (a window billed) — a stale/flat `usedMsToday` (billed
 * only at window boundaries) is ignored rather than snapping the meter
 * back up to the cap (#1329, via {@link budgetReconcileRemainingMs}). Off
 * a reader, or while paused / idle / booting, the display freezes — no VM
 * time is being spent, and a fresh snapshot then lets the server govern.
 *
 * Free learners get a ring + "N min left today" that, under
 * {@link BUDGET_LOW_THRESHOLD_MS}, flips to a warm `M:SS` treatment
 * (seconds only when the urgency is earned) that also names the concrete
 * LOCAL reset time.
 *
 * The pill is a non-interactive status readout in the interim (#1315):
 * with no payment path yet, it makes no purchase promise it can't keep —
 * the "go unlimited" hand-off is retired until checkout exists. Crossing
 * into the low state still fires `runningLowShown` ({@link BUDGET_EVENTS})
 * once per chrome mount — the first funnel beat the out-of-time takeover
 * closes. That SPA-side beat pairs with the backend's authoritative
 * tier-flip (Grafana / #834), not with Umami.
 */

import { useEffect, useRef, useState, type JSX } from 'react'
import { track } from '@labcraft/ui/hooks'
import { useBudget } from '../hooks/index.ts'
import { useReaderChrome } from '../lib/reader_chrome.tsx'
import {
  BUDGET_EVENTS,
  budgetFractionRemaining,
  budgetLiveRemainingMs,
  budgetMinutesLeft,
  budgetReconcileRemainingMs,
  budgetRemainingMs,
  budgetState,
  formatBudgetClock,
  formatResetClock,
  nextBudgetReset,
} from '../lib/budget.ts'
import styles from './budget_meter.module.css'

/** Dash length of the ring's progress arc (matches the SVG geometry). */
const RING_DASH = 41

/** Countdown repaint cadence while the VM runs — one tick a second. */
const TICK_MS = 1_000

/**
 * The chrome budget meter — a live free-tier countdown. See the file
 * header for the running-only tick model and the reconcile baseline.
 *
 * @returns The budget pill, or `null` when no meter applies (paid,
 *   loading, or errored).
 */
export function BudgetMeter(): JSX.Element | null {
  const vmRunning = useReaderChrome()?.vmRunning ?? false
  const budget = useBudget(vmRunning)
  // Fire the running-low funnel beat at most once per chrome mount.
  const firedLowRef = useRef(false)

  const data = budget.data
  const isFree = data?.tier === 'free' && data.limitMs !== null

  // Live-countdown state. The meter extrapolates a monotonic-DOWN
  // countdown from a server-`usedMsToday` baseline rather than refetching
  // each second. `baselineRemainingRef` is the remaining-ms the countdown
  // subtracts from (captured from the server snapshot per the reconcile
  // rule below); `accruedRef` holds closed running spans since that
  // baseline; while a span is open, `runningSinceRef` marks its start.
  // Together: `remaining = baselineRemaining - (accrued + openSpan)`.
  const baselineRemainingRef = useRef<number | null>(null)
  const accruedRef = useRef(0)
  const runningSinceRef = useRef<number | null>(null)
  const [, forceTick] = useState(0)

  // Reconcile each fresh server snapshot into the baseline. The server
  // bills VM time only at usage-window boundaries, so a mid-session
  // snapshot routinely reports a STALE (flat, often still-zero)
  // `usedMsToday`; adopting it verbatim snapped the meter back up to the
  // cap every 30s reconcile (#1329). `budgetReconcileRemainingMs`
  // re-baselines only DOWNWARD while running — a stale/flat snapshot is
  // ignored in favor of the local countdown — and lets the server govern
  // when idle or on the first snapshot. Whatever baseline is adopted, the
  // accrual restarts from it (the elapsed span is already folded in).
  // Keyed on the snapshot instant ONLY — `vmRunning` is read as a
  // point-in-time sample here; its transitions are owned by the effect
  // below, so this must not re-baseline on every pause/resume.
  const snapshotAt = budget.dataUpdatedAt
  useEffect(() => {
    if (!data || data.limitMs === null) return

    const serverRemaining = budgetRemainingMs(data.usedMsToday, data.limitMs)
    const prevBaseline = baselineRemainingRef.current
    const openSpan =
      runningSinceRef.current !== null
        ? Date.now() - runningSinceRef.current
        : 0
    const localRemaining =
      prevBaseline === null
        ? null
        : budgetLiveRemainingMs(prevBaseline, accruedRef.current + openSpan)

    baselineRemainingRef.current = budgetReconcileRemainingMs({
      serverRemainingMs: serverRemaining,
      localRemainingMs: localRemaining,
      vmRunning,
    })
    accruedRef.current = 0
    runningSinceRef.current = vmRunning ? Date.now() : null
    forceTick(t => t + 1)
  }, [snapshotAt])

  // Open/close the running span on each lifecycle transition, so accrual
  // counts only the wall-clock time actually spent `running`.
  useEffect(() => {
    if (vmRunning) {
      if (runningSinceRef.current === null) runningSinceRef.current = Date.now()
    } else if (runningSinceRef.current !== null) {
      accruedRef.current += Date.now() - runningSinceRef.current
      runningSinceRef.current = null
    }
    forceTick(t => t + 1)
  }, [vmRunning])

  // Repaint once a second while running so the countdown visibly ticks.
  // No interval otherwise — a frozen meter needs no work.
  useEffect(() => {
    if (!vmRunning || !isFree) return
    const id = setInterval(() => forceTick(t => t + 1), TICK_MS)
    return () => clearInterval(id)
  }, [vmRunning, isFree])

  const openSpanMs =
    runningSinceRef.current !== null ? Date.now() - runningSinceRef.current : 0
  // Use the reconciled baseline once established; before the first
  // snapshot effect runs, fall back to the raw server remaining so the
  // meter never flashes an exhausted (zero) state on its first paint.
  const baselineRemaining =
    baselineRemainingRef.current ??
    (isFree && data ? budgetRemainingMs(data.usedMsToday, data.limitMs) : 0)
  const remainingMs = isFree
    ? budgetLiveRemainingMs(baselineRemaining, accruedRef.current + openSpanMs)
    : 0
  const state = isFree ? budgetState(remainingMs) : 'healthy'

  useEffect(() => {
    if (isFree && state === 'low' && !firedLowRef.current) {
      firedLowRef.current = true
      track(BUDGET_EVENTS.runningLowShown, {
        minutesLeft: budgetMinutesLeft(remainingMs),
      })
    }
  }, [isFree, state, remainingMs])

  // Paid, loading, or errored → no meter in the chrome.
  if (!data || !isFree || data.limitMs === null) return null

  const fraction = budgetFractionRemaining(remainingMs, data.limitMs)
  const dashOffset = RING_DASH * (1 - fraction)
  const low = state !== 'healthy'
  const resetClock = formatResetClock(nextBudgetReset(new Date()))

  const label =
    state === 'exhausted'
      ? `Out of time · resets ${resetClock}`
      : state === 'low'
        ? `${formatBudgetClock(remainingMs)} left · resets ${resetClock}`
        : `${budgetMinutesLeft(remainingMs)} min left today`

  return (
    <div
      className={`${styles.meter} ${low ? styles.low : ''}`}
      role="status"
      title="Free-tier lab time today"
      aria-label={label}>
      <svg className={styles.ring} viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle
          cx="8"
          cy="8"
          r="6.5"
          className={styles.ringTrack}
          strokeWidth="2"
        />
        <circle
          cx="8"
          cy="8"
          r="6.5"
          className={styles.ringArc}
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray={RING_DASH}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform="rotate(-90 8 8)"
        />
      </svg>
      <span className={styles.label}>{label}</span>
    </div>
  )
}
